import { Box, Chip, ChipProps, Tooltip } from '@mui/material'
import { useKeywordsTranslation } from 'components/Common/ArticleSelector/useKeywordsTranslation'
import { useRALColorAndTranslation } from 'components/RALSelector/useRALColorData'
import { isEqual } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { KeywordDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { markText } from 'utils/textFilterUtils'

type KeywordTagsListProps = {
  keywords: Record<string, KeywordDto[]>
  onClick?: (keywordDto: KeywordDto) => void
}

export function KeywordTagsList(props: KeywordTagsListProps) {
  const { translateKeyword } = useKeywordsTranslation()

  if (!props.keywords) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
      }}
    >
      {Object.values(props.keywords || {})
        ?.flat()
        ?.map((keyword) => (
          <KeywordTag
            key={keyword.originalKeyword}
            token={keyword}
            translateFn={translateKeyword}
            onClick={props.onClick}
          />
        ))}
    </Box>
  )
}

type Props = {
  token: KeywordDto
  translateFn: (token: KeywordDto) => string
  onClick?: (keywordDto: KeywordDto) => void
  highlightText?: string
} & ChipProps

function KeywordTagComp(props: Props) {
  const colorInfo = useRALColorAndTranslation(props.token.originalKeyword)
  const { t } = useTranslation()
  const { token, translateFn, onClick, ...chipProps } = props
  const tooltipTitle = token.category
    ? t(`project:material-category-${token.category}`, token.category)
    : colorInfo?.translatedName || ''

  const TagLabel = (props: { highlightText?: string }) => {
    return (
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {markText(translateFn(token), props.highlightText)}
      </Box>
    )
  }

  return (
    <Tooltip
      key={token.originalKeyword}
      title={tooltipTitle}
      placement="top"
    >
      <Chip
        label={<TagLabel highlightText={props.highlightText} />}
        variant="outlined"
        size="small"
        sx={{
          margin: '0px 2px 4px 0',
          backgroundColor: colorInfo?.color.hex,
          color: (theme) =>
            colorInfo && theme.palette.getContrastText(colorInfo?.color.hex),
          '& :hover': {
            color: (theme) => props.onClick && theme.palette.text.primary,
          },
          ...(chipProps.sx || {}),
        }}
        onClick={props.onClick && (() => onClick?.(token))}
        {...chipProps}
      />
    </Tooltip>
  )
}

export const KeywordTag = React.memo(KeywordTagComp, (prevProps, nextProps) => {
  return isEqual(prevProps.token, nextProps.token)
})


